import _, { get, includes, isFunction } from 'lodash';
import {
  createContext,
  use,
  useCallback,
  useEffect,
  useMemo,
  useState,
  useContext,
} from 'react';
import { useSelector } from 'react-redux';
import {
  useHiddenFieldWarnings,
  useLiveQuerySelector,
  useOpenDiscoveryPresentStateSelector,
  useWithSugarContextFilters,
  useOpenVizSelector,
  useOpenVizChartSpec,
} from '../../common/redux/selectors/viz-selector.hook';
import {
  getSortedQueryData,
  ISortedQueryData,
} from '../../common/redux/selectors/viz-selectors';
import { ChartSpecs } from '../ChartSpecs';
import { Viz } from '../VizUtil';
import { useVizQueryDiscoverAction } from '../../common/redux/actions/discover-actions.hook';
import { useIsAdvancedMode } from '../../common/redux/selectors/main-selector-hooks';
import {
  isEqualByProps,
  isEqualWithoutProps,
} from '../../common/utilities/comparison-functions.util';
import { useHasValueChanged } from '../../common/utilities/state-helpers.hook';
import { useDiscoverOptionSelector } from '../discovery-context/discovery.context';
import { ILayout, IViz } from '../interfaces';
import { IVizChartStateMap } from './viz-chart.interfaces';
import { DynamicFields, VIZ_OPTION_IDS } from '../../common';
import { isDashletMode } from '../../auth/dashlet.util';
import { SugarContext } from '../../../../../mfe/src/internal-messaging';
import { useFeatureFlagProvider } from '../../common/utilities/feature-flag/feature-flag.provider';
import { FEATURE_FLAGS } from '../../common/utilities/feature-flag/feature-flag.constants';

export const useChartValidation = () => {
  const viz = use(VizContext);

  return useMemo(() => {
    const chartSpec = get(ChartSpecs, viz.chartType);
    const _validation =
      isFunction(chartSpec?.validate) && chartSpec.validate(viz);

    if (!_validation?.valid) {
      console.warn('Visualization is not valid', _validation?.error);
    }

    return _validation;
  }, [viz]);
};

export const useChartValidationOld = ({ viz }) => {
  const chartSpec = ChartSpecs[viz.chartType];

  const validation = useMemo(
    () => isFunction(chartSpec?.validate) && chartSpec.validate(viz),
    [chartSpec, viz],
  );

  useEffect(() => {
    if (!validation.valid) {
      console.warn('Visualization is not valid', validation.error);
    }
  }, [validation]);
  const retVal = useMemo(
    () => ({ chartSpec, validation }),
    [chartSpec, validation],
  );
  return retVal;
};

/**
 * this will be deprecated when all charts use the new VizChart
 * @param discoveryId
 */
export const useVizChartStateMap = ({
  vizId: discoveryId,
}): IVizChartStateMap => {
  const useLiveQuery = useLiveQuerySelector({ discoveryId });
  const { value: slicerSelections } = useDiscoverOptionSelector({
    vizId: discoveryId,
    option: VIZ_OPTION_IDS.slicerSelections,
  });

  const convertedFilters = useWithSugarContextFilters({ discoveryId });

  const discovery = useOpenDiscoveryPresentStateSelector({ discoveryId });
  const isAdvancedMode = useIsAdvancedMode();

  const {
    vizLoading,
    viz,
    vizQueryError: queryError,
    dirty,
    queryResults,
    secondaryQueryResults,
  } = useMemo(() => {
    const { vizLoading, viz, vizQueryError, dirty } = discovery;
    const secondaryQueryResults = discovery?.vizQuerySecondaryResults?.data;

    const queryResults: ISortedQueryData = getSortedQueryData(
      discovery,
    ) as ISortedQueryData;

    return {
      vizLoading,
      vizQueryError,
      dirty,
      viz: {
        ...viz,
        options: {
          ...viz?.options,
          slicerSelections: JSON.stringify(slicerSelections),
          filters: JSON.stringify(convertedFilters),
        },
      },
      queryResults,
      secondaryQueryResults,
    };
  }, [convertedFilters, slicerSelections, discovery]);

  return useMemo(() => {
    const { layout } = viz;
    return {
      useLiveQuery,
      dirty,
      vizLoading,
      queryResults,
      secondaryQueryResults,
      queryError,
      viz,
      layout,
      isAdvancedMode,
    };
  }, [
    dirty,
    isAdvancedMode,
    queryError,
    queryResults,
    secondaryQueryResults,
    useLiveQuery,
    viz,
    vizLoading,
  ]);
};

/**
 * this will be deprecated when all charts use the new VizChart
 * @param discoveryId
 */
export const useVizChartDispatchMap = ({
  viz,
  vizId,
  chartSpec,
  variables,
}) => {
  // dashlets do not modify reports, so we assume they have good definitions. This helps dashboard performance
  const skipAnalyze = isDashletMode();

  const doVizQuery = useVizQueryDiscoverAction();
  const vizQuery = useCallback(() => {
    const secondaryQueryVariables =
      isFunction(chartSpec?.buildSecondaryQueryVariables) &&
      chartSpec.buildSecondaryQueryVariables(viz, variables);
    doVizQuery({
      id: vizId,
      variables,
      replaceNulls: true,
      secondaryQueryVariables,
      skipAnalyze,
    });
  }, [chartSpec, doVizQuery, viz, variables, vizId, skipAnalyze]);
  return { vizQuery };
};

/**
 * this will be deprecated when all charts use the new VizChart
 * @param discoveryId
 */
export const useVizChartSelectors = ({ vizId }) => {
  const {
    useLiveQuery,
    vizLoading,
    queryResults,
    secondaryQueryResults,
    queryError,
    viz,
    dirty,
    layout,
    isAdvancedMode,
  } = useVizChartStateMap({ vizId });

  const { chartSpec, validation } = useChartValidationOld({ viz });

  const variables = useMemo(() => Viz.buildQueryVariables(viz), [viz]);
  const { vizQuery } = useVizChartDispatchMap({
    viz, // already sending vizId, remove
    vizId,
    chartSpec,
    variables,
  });

  const retVal = useMemo(
    () => ({
      useLiveQuery,
      chartSpec,
      validation,
      vizLoading,
      dirty,
      queryResults,
      secondaryQueryResults,
      queryError,
      viz,
      layout,
      isAdvancedMode,
      vizQuery,
      variables,
    }),
    [
      useLiveQuery,
      chartSpec, // not needed as vizId is the argument to this hook
      validation, // not needed as vizId is the argument to this hook
      vizLoading, // loading happens as a fallback for use(vizQuery());
      queryResults, // results should return on a synchronous vizQuery call // const dataPromise = use(vizQuery());
      secondaryQueryResults,
      queryError, // see queryResults
      viz, // not needed as vizId is the argument to this hook
      dirty, // unnecessary changing
      layout,
      isAdvancedMode, // unnecessary changing
      vizQuery,
      variables,
    ],
  );
  return retVal;
};

export const buildLayoutSignature = (
  layout?: ILayout,
  shelfIteree?: (arg: any) => string,
) => {
  return _.join(
    _.flatMap(
      _.toPairs(layout as ILayout),
      ([key, shelf]) =>
        `${key}:${_.join(
          _.flatMap(
            shelf ?? [],
            _.isFunction(shelfIteree) ? shelfIteree(shelf) : '',
          ),
          ',',
        )}`,
    ),
    ',',
  );
};

export const useVizChartState = ({ vizId }) => {
  const {
    useLiveQuery,
    chartSpec,
    validation,
    vizLoading,
    queryResults,
    secondaryQueryResults,
    queryError,
    viz,
    layout,
    dirty,
    isAdvancedMode,
    vizQuery,
    variables,
  } = useVizChartSelectors({ vizId });
  const [hasChanged, setHasChanged] = useState(false);
  const vizIdHasChanged = useHasValueChanged({ value: vizId });
  const isUnchangedNewViz = (vizId ?? '').includes('newViz') && !dirty;
  const queryHasChanged = useHasValueChanged({
    value: variables,
    comparator: isEqualWithoutProps('lastDate', 'lastLevel'),
  });
  const emptyLayoutSignature = buildLayoutSignature(_.omit(layout, 'SLICER'));
  const layoutSignature = buildLayoutSignature(
    _.omit(layout, 'SLICER'),
    _.constant('name'),
  );
  const hasLayoutSignatureChanged = useHasValueChanged({
    value: layoutSignature,
    defaultValue: emptyLayoutSignature,
  });
  const hasChartTypeChanged = useHasValueChanged({
    value: chartSpec,
    comparator: isEqualByProps('id'),
    defaultValue: false,
  });
  const layoutHasChanged = hasLayoutSignatureChanged;
  const liveQueryHasChanged = useHasValueChanged({ value: useLiveQuery });

  const fields = useMemo(
    () => [
      ...variables.attributeNames,
      ..._.map(variables.measures, 'attributeName'),
    ],
    [variables.attributeNames, variables.measures],
  );

  const vizAlreadyFetched = useSelector(
    (state: any) => state.discover.discoveryAlreadyRendered,
  );

  const queryFields = useMemo(
    () =>
      _(queryResults?.executeQuery?.columnInfo)
        .reject({ columnType: 'FORMAT' })
        .map('attributeName')
        .reject(_.isNil)
        .value(),
    [queryResults?.executeQuery?.columnInfo],
  );

  const { dynamicValues } = useContext(SugarContext);

  // listens to dashlet context
  const reporteeData =
    dynamicValues[DynamicFields.Forecasts.reportees as string];
  const forecastsTimePeriodStart =
    dynamicValues[DynamicFields.Forecasts.timeperiodstart as string];
  const forecastsTimePeriodEnd =
    dynamicValues[DynamicFields.Forecasts.timeperiodend as string];

  const hasForecastsUserChanged = useHasValueChanged({
    value: reporteeData,
  });
  const hasForecastsTimePeriodStartChanged = useHasValueChanged({
    value: forecastsTimePeriodStart,
  });
  const hasForecastsTimePeriodEndChanged = useHasValueChanged({
    value: forecastsTimePeriodEnd,
  });

  const hasDashletContextChanged =
    hasForecastsUserChanged ||
    hasForecastsTimePeriodStartChanged ||
    hasForecastsTimePeriodEndChanged;

  const discovery = useOpenDiscoveryPresentStateSelector({
    discoveryId: vizId,
  });
  const { isBlocked: hasHiddenFields } = useHiddenFieldWarnings({
    discoveryId: vizId,
  });

  const hasMissingFields =
    !_.isEmpty(discovery.missingFields) || !_.isEmpty(discovery.missingFilters);
  const isBlocked = hasMissingFields || hasHiddenFields;
  const fieldsAreDifferent = !_.isEqual(fields, queryFields);
  const isDashlet = isDashletMode();
  const userChange =
    (vizIdHasChanged ||
      queryHasChanged ||
      layoutHasChanged ||
      liveQueryHasChanged ||
      hasDashletContextChanged ||
      (fieldsAreDifferent && !vizLoading)) &&
    !queryError;
  const shouldRunInitialQuery =
    validation?.valid &&
    _.isNil(queryResults) &&
    _.isNil(queryError) &&
    !vizLoading &&
    (useLiveQuery || !dirty);
  const liveQueryEnabledInDirtyState =
    dirty && useLiveQuery && liveQueryHasChanged;

  useEffect(() => {
    if (userChange && !isBlocked) {
      if (validation?.valid && (useLiveQuery || isDashlet)) {
        if (
          (vizAlreadyFetched && vizIdHasChanged) ||
          (liveQueryHasChanged && useLiveQuery && queryHasChanged)
        ) {
          setHasChanged(true);
        } else if (liveQueryHasChanged && !queryHasChanged) {
          setHasChanged(prevState => {
            return !prevState;
          });
        } else if (hasChartTypeChanged || queryHasChanged) {
          vizQuery();
        }
      } else if (!useLiveQuery || shouldRunInitialQuery) {
        setHasChanged(true);
      }
    } else if (!useLiveQuery && !vizAlreadyFetched && vizLoading) {
      setHasChanged(false);
    }
  }, [
    isDashlet,
    liveQueryHasChanged,
    shouldRunInitialQuery,
    hasChartTypeChanged,
    queryHasChanged,
    useLiveQuery,
    userChange,
    validation?.valid,
    vizAlreadyFetched,
    vizIdHasChanged,
    vizLoading,
    vizQuery,
    isBlocked,
  ]);

  useEffect(() => {
    if (
      !isBlocked &&
      validation.valid &&
      _.isNil(queryResults) &&
      _.isNil(queryError) &&
      !vizLoading &&
      (useLiveQuery || !dirty)
    ) {
      Viz.configureCurrencySymbol(viz);
      vizQuery();
    }
  }, [
    dirty,
    queryError,
    queryResults,
    useLiveQuery,
    validation.valid,
    viz,
    vizLoading,
    vizQuery,
    isBlocked,
  ]);

  const forceQuery = useCallback(() => {
    if (isBlocked) {
      return;
    }
    setHasChanged(false);
    if (validation.valid) {
      vizQuery();
    }
  }, [isBlocked, validation.valid, vizQuery]);

  const willQueryFire =
    validation.valid &&
    ((layoutHasChanged && useLiveQuery) ||
      shouldRunInitialQuery ||
      liveQueryEnabledInDirtyState);
  const isEmptyViz = vars => {
    return _.isEmpty(
      _.flattenDeep([
        vars.attributeNames || [],
        vars.calcs || [],
        vars.measures || [],
        vars.sorts || [],
        vars.subtotals || [],
      ]),
    );
  };
  const retVal = useMemo(
    () => ({
      useLiveQuery,
      liveQueryHasChanged,
      chartSpec,
      validation,
      hasChanged,
      forceQuery,
      vizLoading:
        vizLoading ||
        willQueryFire ||
        (queryHasChanged &&
          !isUnchangedNewViz &&
          !isEmptyViz(variables) &&
          validation.valid &&
          useLiveQuery),
      queryResults,
      secondaryQueryResults,
      queryError,
      viz,
      shouldRunInitialQuery,
      isAdvancedMode,
      dirty,
      hasMissingFields,
      hasHiddenFields,
      isBlocked,
    }),
    [
      useLiveQuery,
      liveQueryHasChanged,
      chartSpec,
      validation,
      hasChanged,
      forceQuery,
      vizLoading,
      willQueryFire,
      queryHasChanged,
      isUnchangedNewViz,
      variables,
      queryResults,
      secondaryQueryResults,
      queryError,
      viz,
      shouldRunInitialQuery,
      isAdvancedMode,
      dirty,
      hasMissingFields,
      hasHiddenFields,
      isBlocked,
    ],
  );
  return retVal;
};

export const VizContext = createContext<Partial<IViz>>({});

// Made so vizId is no longer needed to be passed
export const VizContextProvider = ({ vizId, children }) => {
  const viz = useOpenVizSelector({ discoveryId: vizId });
  const vizContextObj = useMemo<Partial<IViz>>(
    () => ({
      ...viz,
      id: vizId,
    }),
    [viz, vizId],
  );

  return (
    <VizContext.Provider value={vizContextObj}>{children}</VizContext.Provider>
  );
};

export const ChartWindowContext = createContext<{
  height: string | number;
  width: string | number;
}>({
  height: 0,
  width: 0,
});

// @NOTE this utility is used to eventually phase out usage of LegacyVizChart. components as arguments to fix circular dependency issues
export const useGetChartComponent = (vizId: string, Component1, Component2) => {
  const { featureFlags } = useFeatureFlagProvider();
  const { id: chartType } = useOpenVizChartSpec({ discoveryId: vizId });

  return useMemo(() => {
    const allowsVizQueryWatchFlag =
      !!featureFlags[FEATURE_FLAGS.VIZ_QUERY_WATCH];

    const allowsVizQueryWatch =
      includes([ChartSpecs.funnel.id], chartType) && allowsVizQueryWatchFlag;
    return allowsVizQueryWatch ? Component1 : Component2;
  }, [Component1, Component2, chartType, featureFlags]);
};
